import { useState } from 'react';
import {
  AlertTriangle, Ban, CalendarRange, Download, ExternalLink, Globe,
  Loader2, Mail, Plus, RefreshCw, Shield, ShieldAlert,
  ShieldCheck, ShieldX, Trash2, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

import AdminShell from '../components/AdminShell';
import { useAdminSecurity } from '../hooks/useAdminSecurity';
import { blockAdminIp, unblockAdminUser, unblockAdminIp, getAdminSecurityExportUrl } from '../api/admin';
import type { SecurityViolation, SecurityBlockedUser, SecurityBlockedIp } from '../api/admin';
import { ROUTES } from '../constants/routes';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function tenDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 10);
  return d.toISOString().slice(0, 10);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LimiterBadge({ name }: { name: string }) {
  const colours: Record<string, string> = {
    'auth':            'bg-red-950/60 text-red-400 border-red-800/40',
    'password-reset':  'bg-orange-950/60 text-orange-400 border-orange-800/40',
    'analysis-start':  'bg-purple-950/60 text-purple-400 border-purple-800/40',
    'analysis-action': 'bg-violet-950/60 text-violet-400 border-violet-800/40',
    'comment-count':   'bg-blue-950/60 text-blue-400 border-blue-800/40',
    'payment':         'bg-yellow-950/60 text-yellow-400 border-yellow-800/40',
    'oauth':           'bg-cyan-950/60 text-cyan-400 border-cyan-800/40',
    'public-plans':    'bg-teal-950/60 text-teal-400 border-teal-800/40',
  };
  const cls = colours[name] ?? 'bg-zinc-800 text-zinc-400 border-zinc-700';
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {name}
    </span>
  );
}

function ViolationRow({ v }: { v: SecurityViolation }) {
  const hasBlockedUser = v.users.some(u => u.isBlocked);
  return (
    <div className={`rounded-xl border p-4 ${hasBlockedUser ? 'border-red-800/50 bg-red-950/10' : 'border-zinc-800 bg-zinc-900'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-800 text-zinc-400">
            <Globe className="h-4 w-4" />
          </div>
          <div>
            <p className="font-mono text-sm font-semibold text-zinc-100">{v.ip}</p>
            <p className="text-xs text-zinc-500">
              First: {formatDate(v.firstSeen)}
              <span className="mx-1.5 text-zinc-700">·</span>
              Last: {formatDate(v.lastSeen)}
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold ${
          v.hits >= 100 ? 'bg-red-950/60 text-red-400' :
          v.hits >= 30  ? 'bg-orange-950/60 text-orange-400' :
                          'bg-zinc-800 text-zinc-300'
        }`}>
          <Zap className="h-3.5 w-3.5" />
          {v.hits.toLocaleString('en-IN')} blocked hits
        </div>
      </div>

      {v.limiters.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {v.limiters.map(l => <LimiterBadge key={l} name={l} />)}
        </div>
      )}

      {v.users.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {v.users.map(u => (
            <div key={u.id} className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-xs ${
              u.isBlocked ? 'bg-red-950/30 border border-red-800/30' : 'bg-zinc-800/60 border border-zinc-700/40'
            }`}>
              <Mail className="h-3 w-3 flex-shrink-0 text-zinc-500" />
              <span className="font-medium text-zinc-200">{u.email}</span>
              {u.name && <span className="text-zinc-500">({u.name})</span>}
              {u.isBlocked && (
                <span className="ml-auto flex items-center gap-1 rounded-full bg-red-900/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-400">
                  <Ban className="h-2.5 w-2.5" /> Blocked forever
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlockedIpRow({ entry, onUnblocked }: { entry: SecurityBlockedIp; onUnblocked: () => void }) {
  const [unblocking, setUnblocking] = useState(false);

  const handleUnblock = async () => {
    setUnblocking(true);
    try {
      await unblockAdminIp(entry.ip);
      toast.success(`IP ${entry.ip} has been unblocked.`);
      onUnblocked();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to unblock IP.');
    } finally {
      setUnblocking(false);
    }
  };

  return (
    <div className="rounded-xl border border-orange-800/40 bg-orange-950/10 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-orange-950/60 text-orange-400">
            <Globe className="h-4 w-4" />
          </div>
          <div>
            <p className="font-mono text-sm font-semibold text-zinc-100">{entry.ip}</p>
            <p className="mt-0.5 text-xs text-zinc-500">Blocked {formatDate(entry.blockedAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-orange-900/60 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-400">
            <Ban className="h-3 w-3" /> IP Blocked
          </span>
          <button
            onClick={handleUnblock}
            disabled={unblocking}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-700/50 bg-emerald-950/40 px-3 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:border-emerald-600 hover:bg-emerald-950/60 disabled:opacity-50"
          >
            {unblocking ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
            Unblock
          </button>
        </div>
      </div>
      <p className="mt-2.5 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs text-zinc-400">
        {entry.reason}
        {entry.hits > 0 && <span className="ml-2 text-zinc-500">· {entry.hits.toLocaleString('en-IN')} violations recorded</span>}
      </p>
    </div>
  );
}

function BlockedUserRow({ u, onUnblocked }: { u: SecurityBlockedUser; onUnblocked: () => void }) {
  const [unblocking, setUnblocking] = useState(false);

  const handleUnblock = async () => {
    setUnblocking(true);
    try {
      await unblockAdminUser(u.id);
      toast.success(`${u.email} has been unblocked.`);
      onUnblocked();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to unblock user.');
    } finally {
      setUnblocking(false);
    }
  };

  return (
    <div className="rounded-xl border border-red-800/40 bg-red-950/10 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-950/60 text-red-400">
            <Ban className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-zinc-100">{u.email}</p>
              {u.name && <span className="text-xs text-zinc-500">({u.name})</span>}
            </div>
            <p className="mt-0.5 text-xs text-zinc-500">
              Blocked {u.blockedAt ? formatDate(u.blockedAt) : '—'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-red-900/60 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-400">
            <Ban className="h-3 w-3" /> Blocked Forever
          </span>
          <button
            onClick={handleUnblock}
            disabled={unblocking}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-700/50 bg-emerald-950/40 px-3 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:border-emerald-600 hover:bg-emerald-950/60 disabled:opacity-50"
          >
            {unblocking ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
            Unblock
          </button>
        </div>
      </div>

      {u.blockedReason && (
        <p className="mt-2.5 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs text-zinc-400">
          Reason: {u.blockedReason}
        </p>
      )}

      {u.knownIps.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {u.knownIps.map(ip => (
            <span key={ip} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-0.5 font-mono text-[11px] text-zinc-300">
              <Globe className="h-2.5 w-2.5 text-zinc-500" />{ip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Date range picker ────────────────────────────────────────────────────────

interface DateRangeProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

function DateRangePicker({ from, to, onChange }: DateRangeProps) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2">
      <CalendarRange className="h-3.5 w-3.5 flex-shrink-0 text-zinc-500" />
      <input
        type="date"
        value={from}
        max={to}
        onChange={e => onChange(e.target.value, to)}
        className="bg-transparent text-xs text-zinc-300 outline-none [color-scheme:dark] cursor-pointer"
      />
      <span className="text-xs text-zinc-600">to</span>
      <input
        type="date"
        value={to}
        min={from}
        max={today()}
        onChange={e => onChange(from, e.target.value)}
        className="bg-transparent text-xs text-zinc-300 outline-none [color-scheme:dark] cursor-pointer"
      />
    </div>
  );
}

// ─── Manual block IP form ─────────────────────────────────────────────────────

function BlockIpForm({ onBlocked }: { onBlocked: () => void }) {
  const [ip, setIp]         = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen]     = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ip.trim()) return;
    setLoading(true);
    try {
      await blockAdminIp(ip.trim(), reason.trim() || undefined);
      toast.success(`IP ${ip.trim()} blocked.`);
      setIp(''); setReason(''); setOpen(false);
      onBlocked();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to block IP.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-orange-700/50 text-orange-400 hover:border-orange-600 hover:bg-orange-950/30 transition-colors text-xs"
      >
        <Plus className="w-3.5 h-3.5" /> Block IP
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 flex-wrap">
      <input
        type="text"
        placeholder="IP address (e.g. 1.2.3.4)"
        value={ip}
        onChange={e => setIp(e.target.value)}
        required
        className="h-9 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 w-44"
      />
      <input
        type="text"
        placeholder="Reason (optional)"
        value={reason}
        onChange={e => setReason(e.target.value)}
        className="h-9 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 w-44"
      />
      <button
        type="submit"
        disabled={loading || !ip.trim()}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-500 disabled:opacity-50 transition-colors"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldX className="w-3.5 h-3.5" />}
        Block
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="h-9 px-3 rounded-lg border border-zinc-700 text-zinc-400 text-xs hover:text-zinc-100 transition-colors"
      >
        Cancel
      </button>
    </form>
  );
}

// ─── Confirm clear dialog ─────────────────────────────────────────────────────

function ConfirmClearDialog({ onConfirm, onCancel, clearing }: {
  onConfirm: () => void;
  onCancel: () => void;
  clearing: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-950/60 text-red-400">
            <Trash2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold text-zinc-100">Clear violation logs?</p>
            <p className="text-xs text-zinc-500 mt-0.5">Clears violation logs from Redis only.</p>
          </div>
        </div>
        <p className="text-sm text-zinc-400 leading-relaxed mb-5">
          All <span className="text-zinc-200 font-medium">sec:vio:*</span> keys will be deleted from Redis.
          Blocked account records and their associated IPs are stored separately and will
          <span className="text-emerald-400 font-medium"> not</span> be affected.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={clearing}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-50 transition-colors"
          >
            {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Clear logs
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SecurityPage() {
  const [from, setFrom]             = useState(tenDaysAgo());
  const [to, setTo]                 = useState(today());
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const { data, loading, clearing, refresh, clearLogs } = useAdminSecurity(
    { from: from || undefined, to: to || undefined }
  );

  const handleDateChange = (newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
  };

  const handleDownload = () => {
    const url = getAdminSecurityExportUrl({ from: from || undefined, to: to || undefined });
    const a   = document.createElement('a');
    a.href    = url;
    a.click();
  };

  const handleClearConfirm = async () => {
    try {
      const result = await clearLogs();
      toast.success(`Cleared ${result?.clearedKeys ?? 0} log key(s) from Redis.`);
      setShowClearConfirm(false);
      refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to clear logs.');
    }
  };

  return (
    <AdminShell
      title="Security"
      description="Rate limit violations and permanently blocked accounts."
      actions={(
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker from={from} to={to} onChange={handleDateChange} />

          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
          >
            <Download className="w-3.5 h-3.5" />
            Download CSV
          </button>

          <button
            onClick={() => setShowClearConfirm(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-red-800/50 text-red-400 hover:border-red-700 hover:bg-red-950/30 transition-colors text-xs"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clear Logs
          </button>

          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      )}
    >
      {showClearConfirm && (
        <ConfirmClearDialog
          onConfirm={handleClearConfirm}
          onCancel={() => setShowClearConfirm(false)}
          clearing={clearing}
        />
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Summary */}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {([
                  { label: 'Violating IPs',     value: data.summary.totalViolatingIps,                   icon: ShieldAlert, tone: data.summary.totalViolatingIps > 0 ? 'amber' : 'zinc' },
                  { label: 'Total Blocked Hits', value: data.summary.totalHits.toLocaleString('en-IN'),   icon: Zap,         tone: data.summary.totalHits > 100 ? 'red' : 'zinc' },
                  { label: 'Blocked IPs',        value: data.summary.totalBlockedIps,                     icon: Globe,       tone: data.summary.totalBlockedIps > 0 ? 'orange' : 'zinc' },
                  { label: 'Blocked Accounts',   value: data.summary.totalBlockedUsers,                   icon: Ban,         tone: data.summary.totalBlockedUsers > 0 ? 'red' : 'zinc' },
                ] as const).map(({ label, value, icon: Icon, tone }) => {
                  const cls = {
                    amber:  'border-amber-800/30 bg-amber-950/10 text-amber-400',
                    red:    'border-red-800/30 bg-red-950/10 text-red-400',
                    orange: 'border-orange-800/30 bg-orange-950/10 text-orange-400',
                    zinc:   'border-zinc-800 bg-zinc-900 text-zinc-400',
                  }[tone];
                  return (
                    <div key={label} className={`rounded-xl border p-4 ${cls}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-widest text-zinc-500">{label}</p>
                        <Icon className="h-4 w-4" />
                      </div>
                      <p className="mt-2 text-2xl font-bold text-zinc-100">{value}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-zinc-600">
                Auto-block threshold: <span className="text-zinc-400 font-medium">{data.summary.autoBlockThreshold} violations</span> per IP · override with <code className="text-zinc-500">IP_AUTO_BLOCK_THRESHOLD</code> env var
              </p>
            </>
          )}

          {/* Violations */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h2 className="text-base font-semibold text-zinc-100">Rate Limit Violations</h2>
              <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">
                {from && to ? `${from} → ${to}` : 'last 10 days'} · violation logs only · cleared separately from blocked accounts
              </span>
            </div>

            {!data || data.violations.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 py-12 text-center">
                <Shield className="mx-auto h-8 w-8 text-zinc-700" />
                <p className="mt-3 text-sm text-zinc-500">No violations in the selected range.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.violations.map(v => <ViolationRow key={v.ip} v={v} />)}
              </div>
            )}
          </section>

          {/* Blocked IPs */}
          <section>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Globe className="h-4 w-4 text-orange-500" />
              <h2 className="text-base font-semibold text-zinc-100">Blocked IPs</h2>
              <span className="rounded-full bg-orange-950/60 border border-orange-800/30 px-2 py-0.5 text-xs text-orange-400">
                auto-blocked · stored in DB · unblock to restore access
              </span>
              <Link
                to={ROUTES.IP_BLOCKS}
                className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-xs text-zinc-400 hover:text-zinc-100 transition-colors"
              >
                <ShieldX className="h-3 w-3" /> Registration cap blocks <ExternalLink className="h-2.5 w-2.5" />
              </Link>
              <div className="ml-auto">
                <BlockIpForm onBlocked={refresh} />
              </div>
            </div>

            {!data || data.blockedIps.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 py-12 text-center">
                <Shield className="mx-auto h-8 w-8 text-zinc-700" />
                <p className="mt-3 text-sm text-zinc-500">No IPs blocked yet.</p>
                <p className="mt-1 text-xs text-zinc-600">
                  IPs are auto-blocked after {data?.summary.autoBlockThreshold ?? 50} violations.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.blockedIps.map(entry => (
                  <BlockedIpRow key={entry.ip} entry={entry} onUnblocked={refresh} />
                ))}
              </div>
            )}
          </section>

          {/* Blocked accounts */}
          <section>
            <div className="mb-4 flex items-center gap-2">
              <Ban className="h-4 w-4 text-red-500" />
              <h2 className="text-base font-semibold text-zinc-100">Permanently Blocked Accounts</h2>
              <span className="rounded-full bg-red-950/60 border border-red-800/30 px-2 py-0.5 text-xs text-red-400">
                stored in DB · block via Users page · unaffected by log clears
              </span>
            </div>

            {!data || data.blockedUsers.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 py-12 text-center">
                <Shield className="mx-auto h-8 w-8 text-zinc-700" />
                <p className="mt-3 text-sm text-zinc-500">No blocked accounts.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.blockedUsers.map(u => (
                  <BlockedUserRow key={u.id} u={u} onUnblocked={refresh} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </AdminShell>
  );
}
